import { useLayoutEffect, useRef, type RefObject } from "react";
import { useLocation } from "react-router";
import { focusUntouched, shouldMoveFocus } from "./route-focus.ts";

/** How long a new page may take to render its h1 (data, lazy parts) before <main> gets focus. */
const HEADING_WAIT_MS = 3000;

/**
 * After client-side navigation, moves focus to the new page's h1 (or <main> when it
 * has none), so keyboard and screen reader users start on the new page.
 *
 * Render it before <main>: its layout effect then runs before the new page's effects,
 * so it sees where focus was when the navigation started and can leave alone an
 * element the page focuses itself.
 */
export const RouteFocus = ({ mainRef }: { mainRef: RefObject<HTMLElement | null> }): null => {
  const { pathname } = useLocation();
  const previous = useRef<{ pathname: string } | null>(null);

  useLayoutEffect(() => {
    const from = previous.current;
    previous.current = { pathname };
    const main = mainRef.current;
    if (!main || !shouldMoveFocus(from, { pathname })) return;

    const startedOn = document.activeElement;
    const focus = (target: HTMLElement): void => {
      if (!focusUntouched(document.activeElement, startedOn, document.body)) return;
      // Headings are not focusable; -1 keeps them out of the tab order (theme.css hides the ring).
      if (!target.hasAttribute("tabindex")) target.tabIndex = -1;
      // ScrollRestoration owns the scroll position (top, or restored on Back/Forward).
      target.focus({ preventScroll: true });
    };

    let observer: MutationObserver | undefined;
    let giveUp: number | undefined;
    // A tick later, so the page's own effects (which may place focus) have run.
    const start = window.setTimeout(() => {
      const heading = main.querySelector("h1");
      if (heading) {
        focus(heading);
        return;
      }
      // Still loading (data, a lazy part): wait for the heading to render.
      const watcher = new MutationObserver(() => {
        const rendered = main.querySelector("h1");
        if (!rendered) return;
        watcher.disconnect();
        window.clearTimeout(giveUp);
        focus(rendered);
      });
      watcher.observe(main, { childList: true, subtree: true });
      observer = watcher;
      giveUp = window.setTimeout(() => {
        watcher.disconnect();
        focus(main);
      }, HEADING_WAIT_MS);
    }, 0);

    return (): void => {
      window.clearTimeout(start);
      window.clearTimeout(giveUp);
      observer?.disconnect();
    };
  }, [pathname, mainRef]);

  return null;
};
